/**
 * 見出しの横に置く「?」。押すとドロワーでヘルプを読む。
 *
 * ## 画面を離れさせない
 *
 * 見出しのヘルプは「いま触っている画面の読み方」なので、別の画面へ飛ばさず
 * ドロワーで開く。並べて読みたいときのために、ドロワーの中に**別のタブで開く**
 * ボタンを置く（そちらがパーマリンクになる）。
 *
 * ## 説明の無い slug では、何も出さない
 *
 * 押しても空のドロワーが出るだけの「?」を置かない。
 *
 * ## 区画の「?」は、その区画の説明から読ませる
 *
 * `anchor` に見出しの id（`help-anchor.ts` の `headingId`）を渡すと、ドロワーが
 * 開き終わったところでその見出しまで送り、背景を 2 回明滅させて場所を示す。
 * 見出しが見つからなければ何もしない（先頭のまま）。動きを控える設定の
 * 端末では、送るのは一息に、明滅はせず 1 度だけ色を付けて消す。
 *
 * 登録簿はアプリごとに違うので、部品は `createHelpIcon(registry)` で作る。
 */

import { useRef, useState } from "react";
import { Button as AntButton, Drawer, Tooltip } from "antd";
import { QuestionCircleOutlined, ExportOutlined } from "@ant-design/icons";

import { HelpBody } from "./HelpBody";
import { helpPath } from "./help-path";
import type { HelpRegistry } from "./help-registry";

/** 明滅の印。CSS の `@keyframes` が付いている（`styles.css`）。 */
const FLASH_CLASS = "vd-help-flash";

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * ドロワーの本文の中から錨の見出しを探し、そこまで送って明滅させる。
 *
 * 文書の中だけを探す。同じ文書のドロワーが画面に 2 つあると、文書全体では
 * 同じ id が 2 つになるためである。
 */
function revealHelpAnchor(container: HTMLElement, anchor: string): void {
  const target = Array.from(
    container.querySelectorAll<HTMLElement>("h2[id], h3[id]"),
  ).find((heading) => heading.id === anchor);
  if (target === undefined) return;
  const reduced = prefersReducedMotion();
  if (typeof target.scrollIntoView === "function") {
    target.scrollIntoView({
      block: "start",
      behavior: reduced ? "auto" : "smooth",
    });
  }
  // 開き直したときにも明滅させる。付いたままの印を外してから付け直す
  target.classList.remove(FLASH_CLASS);
  void target.offsetWidth;
  target.classList.add(FLASH_CLASS);
  target.addEventListener(
    "animationend",
    () => {
      target.classList.remove(FLASH_CLASS);
    },
    { once: true },
  );
}

export function createHelpIcon(registry: HelpRegistry) {
  return function HelpIcon({
    slug,
    anchor,
  }: {
    slug: string;
    /** 開いたあとに送る見出しの id。無ければ文書の先頭から読ませる */
    anchor?: string;
  }) {
    const [open, setOpen] = useState(false);
    const bodyRef = useRef<HTMLDivElement>(null);
    const doc = registry.findHelpDoc(slug);
    if (doc === null) return null;

    return (
      <>
        <Tooltip title="ヘルプを開く">
          <AntButton
            type="text"
            size="small"
            icon={<QuestionCircleOutlined />}
            aria-label={`${doc.title}のヘルプを開く`}
            onClick={() => {
              setOpen(true);
            }}
          />
        </Tooltip>
        <Drawer
          title={doc.title}
          placement="right"
          width={480}
          open={open}
          onClose={() => {
            setOpen(false);
          }}
          afterOpenChange={(opened) => {
            if (!opened || anchor === undefined) return;
            const container = bodyRef.current;
            if (container === null) return;
            revealHelpAnchor(container, anchor);
          }}
          extra={
            <Tooltip title="別のタブで開く">
              <AntButton
                type="text"
                size="small"
                icon={<ExportOutlined />}
                aria-label={`${doc.title}のヘルプを別のタブで開く`}
                href={helpPath(doc.slug)}
                target="_blank"
                rel="noreferrer noopener"
              />
            </Tooltip>
          }
        >
          <div ref={bodyRef}>
            <HelpBody body={doc.body} />
          </div>
        </Drawer>
      </>
    );
  };
}
